
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  alternateUrlFr?: string;
  alternateUrlEn?: string;
  ogImage?: string;
  pageType?: 'website' | 'article' | 'course';
  structuredData?: object | object[];
  noIndex?: boolean;
  schemaJson?: object | object[];
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  alternateUrlFr,
  alternateUrlEn,
  ogImage = "/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png",
  pageType = "website",
  structuredData,
  noIndex = false,
  schemaJson
}) => {
  const { language } = useLanguage();
  
  const defaultKeywords = language === 'fr' 
    ? "formation ia avocat, formation ia juriste, ia droit, ia juridique, formation continue avocat, formation intelligence artificielle droit, avocat ia, juriste intelligence artificielle"
    : "legal ai training, ai for lawyers, legal artificial intelligence, ai legal tools, legal technology training, lawyer ai training, legal ai course";

  const defaultTitle = language === 'fr' 
    ? "Gaius | Formation IA Juridique pour Avocats"
    : "Gaius | Legal AI Training for Lawyers";

  const siteDescription = language === 'fr'
    ? "Formation IA pour avocats et juristes. Maîtrisez les outils et pratiques d'IA juridique. Audit et conseil."
    : "Expert AI training for lawyers and legal professionals. Master legal AI tools and practices.";

  const finalTitle = title 
    ? (title.toLowerCase().includes('gaius') ? title : `${title} | Gaius`)
    : defaultTitle;
  const finalDescription = description || siteDescription;
  const finalKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  const robotsContent = noIndex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  // Merge structuredData and schemaJson into a single array
  const allSchemas: object[] = [];
  if (structuredData) {
    if (Array.isArray(structuredData)) allSchemas.push(...structuredData);
    else allSchemas.push(structuredData);
  }
  if (schemaJson) {
    if (Array.isArray(schemaJson)) allSchemas.push(...schemaJson);
    else allSchemas.push(schemaJson);
  }

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Gaius - formation IA juridique",
    "alternateName": ["Gaius", "Gaius Legal AI"],
    "url": "https://gaius-tech.com"
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gaius",
    "alternateName": "Gaius Legal AI",
    "url": "https://gaius-tech.com",
    "logo": "https://gaius-tech.com/gaius-logo.webp",
    "image": "https://gaius-tech.com/gaius-logo.webp",
    "description": language === 'fr'
      ? "Cabinet de formation et conseil en intelligence artificielle juridique pour avocats et juristes"
      : "Legal AI training and consulting firm for lawyers and legal professionals",
    "sameAs": [
      "https://gaius-tech.com"
    ]
  };

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="title" content={finalTitle} />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content={robotsContent} />
      
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Hreflang tags for multilingual SEO */}
      {alternateUrlFr && <link rel="alternate" hrefLang="fr" href={alternateUrlFr} />}
      {alternateUrlEn && <link rel="alternate" hrefLang="en" href={alternateUrlEn} />}
      {canonicalUrl && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}
      
      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={pageType} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `https://gaius-tech.com${ogImage}`} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:site_name" content="Gaius - formation IA juridique" />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `https://gaius-tech.com${ogImage}`} />
      <meta name="twitter:site" content="@gaius_legal" />

      {/* WebSite schema for Google site name in search results */}
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      {/* Organization schema for Google logo in search results */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {allSchemas.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};
